
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../../components/feature/Sidebar';
import { TopBar } from '../../components/feature/TopBar';
import { Button } from '../../components/atoms/Button';

import { KPICards } from './components/KPICards';
import { EnergyFlowDiagram } from './components/EnergyFlowDiagram';
import { TodayChart } from './components/TodayChart';
import { SystemAlerts } from './components/SystemAlerts';

interface SystemData {
  powerFlow: {
    P_PV: number;
    P_Load: number;
    P_Grid: number;
    rel_Autonomy: number;
    rel_SelfConsumption: number;
    Battery?: {
      SOC: number;
      P_Battery: number;
      Status: string;
    };
  };
  inverter: {
    PAC: number;
    Temperature: number;
    DeviceStatus: {
      StatusCode: number;
      ErrorCode: number;
    };
  };
  meter: {
    PowerReal_P_Sum: number;
    EnergyReal_WAC_Sum_Produced: number;
    EnergyReal_WAC_Sum_Consumed: number;
  };
}

export default function HomePage() {
  const navigate = useNavigate();
  const [systemData, setSystemData] = useState<SystemData | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verificar autenticación
    const isAuthenticated = localStorage.getItem('is_authenticated');
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Simular datos en tiempo real desde API Fronius
    const fetchSystemData = async () => {
      setLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 800));

        // Simular datos realistas del sistema solar
        const currentHour = new Date().getHours();
        const isDaytime = currentHour >= 6 && currentHour <= 18;

        const pvPower = isDaytime ? Math.floor(Math.random() * 2000) + 1500 : 0;
        const loadPower = Math.floor(Math.random() * 800) + 1200;
        const gridPower = loadPower - pvPower;

        setSystemData({
          powerFlow: {
            P_PV: pvPower,
            P_Load: loadPower,
            P_Grid: gridPower,
            rel_Autonomy: Math.min(100, Math.floor((pvPower / loadPower) * 100)),
            rel_SelfConsumption: Math.min(100, Math.floor((Math.min(pvPower, loadPower) / pvPower) * 100)),
            Battery: {
              SOC: Math.floor(Math.random() * 40) + 60,
              P_Battery: Math.floor(Math.random() * 1000) - 500,
              Status: Math.random() > 0.5 ? 'Charging' : 'Discharging'
            }
          },
          inverter: {
            PAC: pvPower,
            Temperature: Math.floor(Math.random() * 20) + 35,
            DeviceStatus: {
              StatusCode: 7,
              ErrorCode: 0
            }
          },
          meter: {
            PowerReal_P_Sum: gridPower,
            EnergyReal_WAC_Sum_Produced: Math.floor(Math.random() * 50000) + 100000,
            EnergyReal_WAC_Sum_Consumed: Math.floor(Math.random() * 30000) + 80000
          }
        });

        setLastUpdate(new Date());
      } catch (error) {
        console.error('Error al obtener datos del sistema:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSystemData();
    return () => { };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 ml-[var(--sidebar-width)]">
        <TopBar title="Inicio" lastUpdate={lastUpdate} />

        <div className="p-6">
          {/* Hero Section */}
          <div className="relative overflow-hidden rounded-3xl mb-8 bg-gradient-to-r from-blue-900 to-indigo-900 shadow-xl">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center mix-blend-overlay"></div>
            </div>
            <div className="relative p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="max-w-2xl text-white space-y-4">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-200 text-sm font-medium backdrop-blur-sm">
                  <span className="w-2 h-2 rounded-full bg-green-400 mr-2 animate-pulse"></span>
                  Sistema Operativo
                </div>
                <h1 className="text-4xl md:text-5xl font-bold leading-tight tracking-tight">
                  Bienvenido a tu <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Energía Inteligente</span>
                </h1>
                <p className="text-lg text-blue-100/90 max-w-xl leading-relaxed">
                  Tu sistema solar está generando energía limpia. Monitorea tu producción, optimiza tu consumo y maximiza tus ahorros en tiempo real.
                </p>
                <div className="flex flex-wrap gap-4 pt-4">
                  <Button
                    variant="primary"
                    size="lg"
                    className="bg-white text-blue-900 hover:bg-blue-50 border-none shadow-lg shadow-blue-900/20 font-semibold px-8"
                    onClick={() => navigate('/reports')}
                  >
                    Ver Reporte Mensual
                  </Button>
                  <Button
                    variant="secondary"
                    size="lg"
                    className="bg-blue-800/50 text-white border-blue-400/30 hover:bg-blue-800/70 backdrop-blur-sm"
                    onClick={() => navigate('/devices')}
                  >
                    Mis Dispositivos
                  </Button>
                </div>
              </div>

              {/* Quick Stats Card */}
              <div className="w-full md:w-auto min-w-[300px]">
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-white shadow-2xl">
                  <h3 className="text-blue-200 text-sm font-medium mb-4 uppercase tracking-wider">Resumen de Hoy</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-yellow-500/20 rounded-lg">
                          <i className="ri-sun-line text-yellow-400 text-xl"></i>
                        </div>
                        <span className="text-white/90">Producción</span>
                      </div>
                      <span className="text-xl font-bold">{systemData ? ((systemData.meter.EnergyReal_WAC_Sum_Produced % 1000) / 10).toFixed(1) : '--'} kWh</span>
                    </div>
                    <div className="w-full h-px bg-white/10"></div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-500/20 rounded-lg">
                          <i className="ri-home-line text-blue-400 text-xl"></i>
                        </div>
                        <span className="text-white/90">Consumo</span>
                      </div>
                      <span className="text-xl font-bold">{systemData ? ((systemData.meter.EnergyReal_WAC_Sum_Consumed % 1000) / 10).toFixed(1) : '--'} kWh</span>
                    </div>
                    <div className="w-full h-px bg-white/10"></div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-500/20 rounded-lg">
                          <i className="ri-leaf-line text-green-400 text-xl"></i>
                        </div>
                        <span className="text-white/90">Ahorro Est.</span>
                      </div>
                      <span className="text-xl font-bold text-green-400">$2.450</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div
              onClick={() => navigate('/reports')}
              className="group bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:border-blue-200 transition-all cursor-pointer relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
              <div className="relative">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center text-2xl mb-4 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                  <i className="ri-file-chart-line"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Reportes y Análisis</h3>
                <p className="text-sm text-gray-500 mb-4">Descarga historiales detallados de producción y consumo.</p>
                <span className="text-blue-600 text-sm font-medium flex items-center group-hover:translate-x-1 transition-transform">
                  Ver reportes <i className="ri-arrow-right-line ml-1"></i>
                </span>
              </div>
            </div>

            <div
              onClick={() => navigate('/devices')}
              className="group bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:border-purple-200 transition-all cursor-pointer relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-purple-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
              <div className="relative">
                <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center text-2xl mb-4 group-hover:bg-purple-600 group-hover:text-white transition-colors">
                  <i className="ri-server-line"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Mis Equipos</h3>
                <p className="text-sm text-gray-500 mb-4">Gestiona tus plantas, inversores y medidores inteligentes.</p>
                <span className="text-purple-600 text-sm font-medium flex items-center group-hover:translate-x-1 transition-transform">
                  Gestionar equipos <i className="ri-arrow-right-line ml-1"></i>
                </span>
              </div>
            </div>

            <div
              className="group bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 shadow-sm border border-green-100 hover:shadow-md transition-all relative overflow-hidden"
            >
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-white text-green-600 rounded-xl flex items-center justify-center text-2xl shadow-sm">
                    <i className="ri-plant-line"></i>
                  </div>
                  <span className="bg-green-200 text-green-800 text-xs font-bold px-2 py-1 rounded-full">ECO</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Impacto Ambiental</h3>
                <div className="space-y-3 mt-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">CO₂ Evitado</span>
                    <span className="font-bold text-gray-900">1.2 Ton</span>
                  </div>
                  <div className="w-full bg-green-200/50 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full w-[75%]"></div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Árboles Eq.</span>
                    <span className="font-bold text-gray-900">45 Árboles</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-blue-200 border-t-cyan-600 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600 font-medium">Conectando con sistema Fronius...</p>
                <p className="text-gray-500 text-sm mt-2">Obteniendo datos en tiempo real</p>
              </div>
            </div>
          ) : (
            <>
              {/* Alertas del Sistema */}
              <div className="mb-6">
                <SystemAlerts systemData={systemData} />
              </div>

              {/* KPIs Instantáneos */}
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-dashboard-3-line text-cyan-600 w-5 h-5 flex items-center justify-center text-xl mr-2"></i>
                  KPIs en Tiempo Real
                </h2>
                <KPICards systemData={systemData} />
              </div>

              {/* Flujo Energético */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <i className="ri-flashlight-line text-cyan-600 w-5 h-5 flex items-center justify-center text-xl mr-2"></i>
                    Flujo Energético en Vivo
                  </h2>
                  <EnergyFlowDiagram powerFlowData={systemData?.powerFlow} />
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <i className="ri-line-chart-line text-blue-600 w-5 h-5 flex items-center justify-center text-xl mr-2"></i>
                    Gráfica del Día
                  </h2>
                  <TodayChart />
                </div>
              </div>

              {/* Resumen Energético */}
              {systemData && (
                <div className="bg-gradient-to-r from-blue-50 to-cyan-100 rounded-xl shadow-sm p-6 border border-cyan-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center">
                      <i className="ri-lightbulb-line text-white w-5 h-5 flex items-center justify-center text-xl"></i>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Análisis Energético Instantáneo
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div className="bg-white/70 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-2xl">
                          {systemData.powerFlow.P_Load > systemData.powerFlow.P_PV ? '⚠️' : '✅'}
                        </span>
                        <span className="font-semibold text-gray-800">Balance Energético</span>
                      </div>
                      <p className="text-gray-700">
                        {systemData.powerFlow.P_Load > systemData.powerFlow.P_PV
                          ? `Déficit: ${systemData.powerFlow.P_Load - systemData.powerFlow.P_PV}W`
                          : `Superávit: ${systemData.powerFlow.P_PV - systemData.powerFlow.P_Load}W`
                        }
                      </p>
                    </div>

                    <div className="bg-white/70 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-2xl">
                          {systemData.powerFlow.P_Grid > 0 ? '📥' : systemData.powerFlow.P_Grid < 0 ? '📤' : '⚖️'}
                        </span>
                        <span className="font-semibold text-gray-800">Estado de Red</span>
                      </div>
                      <p className="text-gray-700">
                        {systemData.powerFlow.P_Grid > 0
                          ? `Comprando ${systemData.powerFlow.P_Grid}W`
                          : systemData.powerFlow.P_Grid < 0
                            ? `Vendiendo ${Math.abs(systemData.powerFlow.P_Grid)}W`
                            : `Sin intercambio`
                        }
                      </p>
                    </div>

                    <div className="bg-white/70 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-2xl">🔋</span>
                        <span className="font-semibold text-gray-800">Batería</span>
                      </div>
                      <p className="text-gray-700">
                        {systemData.powerFlow.Battery?.SOC}% - {systemData.powerFlow.Battery?.Status}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
